import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  Camera, FileText, Truck, XCircle, Download, Filter, FilterX,
  Calendar, CheckCircle, Info
} from 'lucide-react';
import { Pie } from 'react-chartjs-2';
import { 
  Chart as ChartJS, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  Title 
} from 'chart.js';
import * as XLSX from 'xlsx';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

const EvidenceDashboard = () => {
  // State variables
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [stats, setStats] = useState({
    photo_evidence: { loaded: 0, not_loaded: 0 },
    pdf_evidence: { loaded: 0, not_loaded: 0 },
    total_shipments: 0
  });
  const [allShipments, setAllShipments] = useState([]); // Store all shipments
  const [filteredShipments, setFilteredShipments] = useState([]); // Store filtered shipments
  
  // Estado para rastrear filtros de gráficas
  const [chartFilter, setChartFilter] = useState({
    type: null, // 'photo' o 'pdf'
    value: null, // 'loaded' o 'not_loaded'
  });
  
  // Estado para notificación de ayuda
  const [showChartHelp, setShowChartHelp] = useState(false);
  
  // Estado para controlar qué dropdowns están abiertos
  const [openDropdowns, setOpenDropdowns] = useState({
    vendors: false,
    carriers: false,
    statuses: false
  });
  
  // Filter options
  const [filterOptions, setFilterOptions] = useState({
    clients: [],
    carriers: [],
    statuses: [],
    vendors: []
  });
  
  // Current filters
  const [filters, setFilters] = useState({
    date_range: [null, null], // Rango para fecha de carga
    delivery_date_range: [null, null], // Nuevo: Rango para cita de entrega
    cliente_id: '',
    carrier_id: [],  // Cambiado a array para selección múltiple
    status: [],      // Cambiado a array para selección múltiple
    vendor: []       // Cambiado a array para selección múltiple
  });

  // Ref for filter form
  const filterFormRef = useRef(null);

  // Load all shipments data on component mount
  useEffect(() => {
    const fetchData = async () => {
      await loadAllShipments();
      
      // Mostrar ayuda sobre gráfico clickeable después de cargar datos
      setTimeout(() => {
        setShowChartHelp(true);
        setTimeout(() => {
          setShowChartHelp(false);
        }, 5000); // Ocultar después de 5 segundos
      }, 1000);
    };
    
    fetchData();
  }, []);

  // Update filtered stats when filtered shipments change
  useEffect(() => {
    if (filteredShipments && filteredShipments.length > 0) {
      const photo_evidence_loaded = filteredShipments.filter(s => s.has_photo_evidence).length;
      const pdf_evidence_loaded = filteredShipments.filter(s => s.has_pdf_evidence).length;
      
      setStats({
        photo_evidence: {
          loaded: photo_evidence_loaded,
          not_loaded: filteredShipments.length - photo_evidence_loaded
        },
        pdf_evidence: {
          loaded: pdf_evidence_loaded,
          not_loaded: filteredShipments.length - pdf_evidence_loaded
        },
        total_shipments: filteredShipments.length
      });
    }
  }, [filteredShipments]);

  // Update chart filter when it changes
  useEffect(() => {
    if (chartFilter.type && chartFilter.value !== null) {
      applyChartFilter();
    } else if (chartFilter.type === null && chartFilter.value === null) {
      // Si se ha limpiado el filtro de gráficos, volvemos a aplicar los filtros manuales si existen
      if (Object.values(filters).some(val => val !== '')) {
        applyFilters();
      } else {
        // Si no hay filtros manuales, mostrar todos los embarques
        setFilteredShipments(allShipments);
      }
    }
  }, [chartFilter]);

  // Apply filters when filter values change
  useEffect(() => {
    // Only apply if any filter has a value
    if (Object.values(filters).some(value => value !== '')) {
      applyFilters();
    }  }, [filters]);
  // Función para consolidar métricas de órdenes agrupadas por Stop
  const consolidateMetricsByStop = (orders) => {
    if (!orders || orders.length === 0) return null;

    // Agrupar órdenes por Stop
    const ordersByStop = orders.reduce((acc, order) => {
      const stop = order.Stop || 'unknown';
      if (!acc[stop]) acc[stop] = [];
      acc[stop].push(order);
      return acc;
    }, {});

    // Consolidar métricas para cada Stop
    const metricsByStop = {};
    
    // Para cada Stop, buscar las métricas relevantes
    Object.entries(ordersByStop).forEach(([stop, stopsOrders]) => {
      // Buscar la primera orden que tenga métricas para este Stop
      const orderWithMetrics = stopsOrders.find(order => 
        order.metrics && Object.keys(order.metrics).length > 0
      );
      
      if (orderWithMetrics && orderWithMetrics.metrics) {
        // Si las métricas ya son un objeto (no un array), usarlas directamente
        metricsByStop[stop] = orderWithMetrics.metrics;
      }
    });
      // Si hay un solo stop, devolver las métricas directamente
    if (Object.keys(metricsByStop).length === 1) {
      const stop = Object.keys(metricsByStop)[0];
      return metricsByStop[stop]; 
    } 
    // Si hay múltiples stops, encontrar el último (el de mayor número) para calcular retrasos
    // ya que es cuando realmente se finaliza el viaje
    else if (Object.keys(metricsByStop).length > 0) {
      const stops = Object.keys(metricsByStop)
        .filter(stop => stop !== 'unknown')
        .map(stop => parseInt(stop, 10))
        .filter(stop => !isNaN(stop))
        .sort((a, b) => b - a); // Ordenamos de mayor a menor para obtener el último stop
      
      if (stops.length > 0) {
        return metricsByStop[stops[0].toString()];
      } else if (metricsByStop['unknown']) {
        return metricsByStop['unknown'];
      }
    }
    
    return null;
  };

  // Load all shipments from the backend
  const loadAllShipments = async () => {
    setLoading(true);
    try {
      // Fetch all shipments without filters
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/v1/compliance-dashboard`);
      const shipments = response.data;
        // Log para depurar
      console.log("Shipments recibidos:", shipments.slice(0, 3)); // Mostrar los primeros 3 para no llenar la consola
      
      // Verificar si hay órdenes con métricas
      const hasMetrics = shipments.some(s => 
        s.orders && 
        s.orders.some(o => o.metrics && o.metrics.length > 0)
      );
      console.log("¿Hay órdenes con métricas?", hasMetrics);
      
      // Format shipments data based on the actual data structure
      const formattedShipments = shipments.map(s => {
        // Normalizar fechas - Ajuste para zona horaria local
        let pickupDate = null;
        if (s.pickup_date) {
          // Crear fecha y establecer a las 00:00:00 del día (hora local)
          pickupDate = new Date(s.pickup_date);
          // Importante: Mantener solo el año, mes y día para comparaciones consistentes
          pickupDate = new Date(pickupDate.getFullYear(), pickupDate.getMonth(), pickupDate.getDate());
        }
        
        // Normalizar fecha de creación
        const createdAtDate = new Date(s.created_at || new Date());
        const normalizedCreatedAtDate = new Date(createdAtDate.getFullYear(), createdAtDate.getMonth(), createdAtDate.getDate());
          // Extraer Lt del shipment - detectar en qué campo viene
        let transportista = 'N/A';
        if (s.Lt) {
          transportista = s.Lt;
        } else if (s.carrier_name) {
          transportista = s.carrier_name;
        } else if (s.carrier && s.carrier.name) {
          transportista = s.carrier.name;
        }
        
        // Extraer información del camión (truck)
        let truck = 'N/A';
        if (s.Truck) {
          truck = s.Truck;
        } else if (s.vehicle) {
          truck = s.vehicle;
        }
        
        // Obtener la cita de entrega (Appointment) de las órdenes asociadas
        // agrupadas por paradas
        let deliveryDate = null;
        let deliveryDatesByStop = {};
        let deliveryDateDisplay = 'N/A';
        
        // Si el embarque tiene órdenes asociadas
        if (s.orders && s.orders.length > 0) {
          // Agrupar órdenes por Stop
          const ordersByStop = s.orders.reduce((acc, order) => {
            const stop = order.Stop || 'unknown';
            if (!acc[stop]) acc[stop] = [];
            acc[stop].push(order);
            return acc;
          }, {});
          
          // Para cada Stop, encontrar la fecha más temprana
          Object.entries(ordersByStop).forEach(([stop, orders]) => {
            const ordersWithAppointments = orders.filter(order => 
              order.Appointment && order.Appointment.trim() !== ''
            );
            
            if (ordersWithAppointments.length > 0) {
              const appointmentDates = ordersWithAppointments.map(order => {
                // Extraer fecha y hora si están disponibles
                const datePart = order.Appointment.split('T')[0];
                const timePart = order.Appointment_hr || '00:00';
                
                // Crear objeto Date combinando fecha y hora
                const [year, month, day] = datePart.split('-').map(Number);
                const [hours, minutes] = timePart.split(':').map(Number);
                
                return new Date(year, month - 1, day, hours, minutes);
              });
              
              // Encontrar la fecha más temprana para este stop
              const earliestDateForStop = new Date(Math.min(...appointmentDates.map(d => d.getTime())));
              deliveryDatesByStop[stop] = {
                date: earliestDateForStop,
                display: earliestDateForStop.toLocaleDateString('es-MX', {
                  day: '2-digit', month: '2-digit', year: 'numeric',
                  hour: '2-digit', minute: '2-digit'
                })
              };
              
              // Actualizar la fecha de entrega global (la más temprana de todas las paradas)
              if (!deliveryDate || earliestDateForStop < deliveryDate) {
                deliveryDate = earliestDateForStop;
              }
            }
          });
          
          // Formatear para mostrar según el número de paradas
          if (Object.keys(deliveryDatesByStop).length === 0) {
            deliveryDateDisplay = 'N/A';
          } else if (Object.keys(deliveryDatesByStop).length === 1) {
            // Si solo hay una parada, mostrar la fecha directamente
            const stop = Object.keys(deliveryDatesByStop)[0];
            deliveryDateDisplay = deliveryDatesByStop[stop].display;
          } else {
            // Si hay múltiples paradas, indicarlo
            deliveryDateDisplay = `${Object.keys(deliveryDatesByStop).length} paradas`;
          }
        }
          return {
          id: s.id,
          folio: s.folio || '',
          reference: s.reference || 'N/A',
          created_at: s.created_at || new Date().toISOString(),
          pickup_date: s.pickup_date || null,
          delivery_date: deliveryDate || s.delivery_date || null,
          delivery_date_display: deliveryDateDisplay,
          delivery_dates_by_stop: deliveryDatesByStop,
          status: s.status || 'PENDIENTE',
          substatus: s.Substatus || '',
          origin: s.origin || 'N/A',
          destination: s.destination || 'N/A',
          carrier_name: transportista,
          Lt: transportista, // Asegurarnos que Lt siempre tiene un valor
          carrier_id: s.carrier_id || s.id_linea_transporte,
          client_name: s.customer || s.vendor || s.client_name || 'N/A',
          cliente_id: s.cliente_id || s.id_cuenta,
          truck: truck, // Añadir el campo truck
          has_photo_evidence: Boolean(s.imagen_evidencia || s.photos_drive_ids || s.photo_evidence),
          has_pdf_evidence: Boolean(s.pdf_carta_porte || s.evidence_drive_id || s.pdf_evidence),
          created_at_date: normalizedCreatedAtDate, // Fecha normalizada (solo año, mes, día)
          pickup_date_formatted: pickupDate, // Fecha normalizada (solo año, mes, día)          // Información adicional que puede ser útil para filtros
          vendor: s.vendor || 'N/A',
          driver: s.driver || 'N/A',          vehicle: s.vehicle || 'N/A',
          orders: s.orders || [], // Mantener las órdenes asociadas
          // Consolidar métricas de las órdenes por Stop
          metrics: consolidateMetricsByStop(s.orders), 
          // Fechas formateadas para mostrar en la UI
          pickup_date_display: pickupDate ? pickupDate.toLocaleDateString('es-MX', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
          }) : 'N/A',
          created_at_display: normalizedCreatedAtDate.toLocaleDateString('es-MX', {
            day: '2-digit', month: '2-digit', year: 'numeric'
          })
        };
      });      
      console.log("DEBUG - Formattedshipments length:", formattedShipments.length);
      
      // Verificar las métricas consolidadas en los primeros 3 shipments
      console.log("Métricas consolidadas:", formattedShipments.slice(0, 3).map(s => ({
        reference: s.reference,
        metrics: s.metrics
      })));
      
      // Procesar las opciones de filtro directamente con los datos formateados
      if (formattedShipments && formattedShipments.length > 0) {
        console.log("Cargando opciones de filtro desde", formattedShipments.length, "embarques");
        
        // Extraer vendors únicos (conservando mayúsculas/minúsculas originales)
        const uniqueVendors = Array.from(
          new Set(
            formattedShipments
              .filter(s => s.vendor && s.vendor !== 'N/A')
              .map(s => s.vendor)
          )
        ).sort();
        
        console.log("DEBUG - Vendors encontrados:", uniqueVendors);

        // Extraer clientes únicos
        const clientsMap = new Map();
        formattedShipments.forEach(s => {
          if (s.cliente_id && s.client_name) {
            clientsMap.set(s.cliente_id, {
              id: s.cliente_id,
              name: s.client_name
            });
          }
        });
        const uniqueClients = Array.from(clientsMap.values());
        
        console.log("DEBUG - Clientes encontrados:", uniqueClients);
        
        // Extraer transportistas únicos con formato consistente
        const carriersMap = new Map();
        formattedShipments.forEach(s => {
          if (s.carrier_name && s.carrier_name !== 'N/A') {
            const normalizedName = s.carrier_name.trim();
            carriersMap.set(normalizedName, {
              id: normalizedName,
              name: normalizedName
            });
          }
        });
        const uniqueCarriers = Array.from(carriersMap.values());
        
        console.log("DEBUG - Transportistas encontrados:", uniqueCarriers);
        
        // Extraer estados únicos
        const uniqueStatuses = Array.from(
          new Set(
            formattedShipments
              .filter(s => s.status)
              .map(s => s.status)
          )
        ).sort();
        
        console.log("DEBUG - Estados encontrados:", uniqueStatuses);
        
        // Guardar las opciones de filtro
        setFilterOptions({
          vendors: uniqueVendors,
          clients: uniqueClients,
          carriers: uniqueCarriers,
          statuses: uniqueStatuses
        });
      } else {
        console.warn('No se encontraron embarques para cargar opciones de filtro.');
      }
      
      // Actualizar el estado después de procesar todo
      setAllShipments(formattedShipments);
      setFilteredShipments(formattedShipments);
      
      // Initial statistics calculation
      const photo_evidence_loaded = formattedShipments.filter(s => s.has_photo_evidence).length;
      const pdf_evidence_loaded = formattedShipments.filter(s => s.has_pdf_evidence).length;
      
      setStats({
        photo_evidence: {
          loaded: photo_evidence_loaded,
          not_loaded: formattedShipments.length - photo_evidence_loaded
        },
        pdf_evidence: {
          loaded: pdf_evidence_loaded,
          not_loaded: formattedShipments.length - pdf_evidence_loaded
        },
        total_shipments: formattedShipments.length
      });
      
    } catch (error) {
      console.error('Error loading shipments data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Apply filters to all shipments
  const applyFilters = () => {
    let results = [...allShipments];
    
    // Filter by date range
    if (filters.date_range[0] && filters.date_range[1]) {
      const startDate = new Date(filters.date_range[0]);
      startDate.setHours(0, 0, 0, 0); // Inicio del día
      
      const endDate = new Date(filters.date_range[1]);
      endDate.setHours(23, 59, 59, 999); // Final del día
      
      // Verificar si las fechas son del mismo día (comparando solo año, mes, día)
      const isSameDay = startDate.getFullYear() === endDate.getFullYear() && 
                         startDate.getMonth() === endDate.getMonth() && 
                         startDate.getDate() === endDate.getDate();
      
      results = results.filter(s => {
        const dateToCompare = s.pickup_date_formatted || s.created_at_date;
        if (!dateToCompare) return false;
        
        // Crear una copia de la fecha para no modificar la original
        const dateForComparison = new Date(dateToCompare);
        
        if (isSameDay) {
          // Si es el mismo día, comparamos solo año, mes y día
          return dateForComparison.getFullYear() === startDate.getFullYear() && 
                 dateForComparison.getMonth() === startDate.getMonth() && 
                 dateForComparison.getDate() === startDate.getDate();
        } else {
          // Si es un rango de fechas, usamos la comparación normal
          return dateForComparison >= startDate && dateForComparison <= endDate;
        }
      });
    }
    
    // Filter by delivery date range
    if (filters.delivery_date_range[0] && filters.delivery_date_range[1]) {
      const startDate = new Date(filters.delivery_date_range[0]);
      startDate.setHours(0, 0, 0, 0); // Inicio del día
      
      const endDate = new Date(filters.delivery_date_range[1]);
      endDate.setHours(23, 59, 59, 999); // Final del día
      
      // Verificar si las fechas son del mismo día (comparando solo año, mes, día)
      const isSameDay = startDate.getFullYear() === endDate.getFullYear() && 
                         startDate.getMonth() === endDate.getMonth() && 
                         startDate.getDate() === endDate.getDate();
      
      results = results.filter(s => {
        // Verificar primero la fecha general de entrega
        if (s.delivery_date) {
          const dateForComparison = new Date(s.delivery_date);
          
          if (isSameDay) {
            if (dateForComparison.getFullYear() === startDate.getFullYear() && 
                dateForComparison.getMonth() === startDate.getMonth() && 
                dateForComparison.getDate() === startDate.getDate()) {
              return true;
            }
          } else if (dateForComparison >= startDate && dateForComparison <= endDate) {
            return true;
          }
        }
        
        // Si no coincidió con la fecha principal, verificar todas las paradas
        if (s.delivery_dates_by_stop && Object.keys(s.delivery_dates_by_stop).length > 0) {
          // Verificar si alguna de las paradas tiene una cita dentro del rango
          return Object.values(s.delivery_dates_by_stop).some(stopData => {
            const dateForComparison = new Date(stopData.date);
            
            if (isSameDay) {
              return dateForComparison.getFullYear() === startDate.getFullYear() && 
                     dateForComparison.getMonth() === startDate.getMonth() && 
                     dateForComparison.getDate() === startDate.getDate();
            } else {
              return dateForComparison >= startDate && dateForComparison <= endDate;
            }
          });
        }
        
        return false; // Si no hay fechas de entrega, no coincide
      });
    }
    
    // Filter by cliente_id (permite filtrado por ID o por nombre)
    if (filters.cliente_id) {
      results = results.filter(s => {
        // Intentar coincidencia por ID
        if (s.cliente_id === parseInt(filters.cliente_id)) return true;
        
        // O por nombre del cliente si coincide con algún cliente en filterOptions
        const selectedClient = filterOptions.clients.find(c => c.id === parseInt(filters.cliente_id));
        if (selectedClient && s.client_name) {
          const clientNameLower = s.client_name.toLowerCase();
          const selectedClientNameLower = selectedClient.name.toLowerCase();
          if (clientNameLower.includes(selectedClientNameLower)) {
            return true;
          }
        }
        
        return false;
      });
    }
    
    // Filter by carrier_id (usando el campo Lt o carrier_name)
    if (filters.carrier_id && filters.carrier_id.length > 0) {
      results = results.filter(s => {
        if (!s.Lt && !s.carrier_name) return false;
        
        const carrierToCheck = (s.Lt || s.carrier_name).toLowerCase().trim();
        return filters.carrier_id.some(id => carrierToCheck === id.toLowerCase().trim());
      });
    }
    
    // Filter by vendor
    if (filters.vendor && filters.vendor.length > 0) {
      results = results.filter(s => {
        if (!s.vendor) return false;
        
        const vendorToCheck = s.vendor.toLowerCase().trim();
        return filters.vendor.some(v => vendorToCheck === v.toLowerCase().trim());
      });
    }
    
    // Filter by status (case insensitive)
    if (filters.status && filters.status.length > 0) {
      results = results.filter(s => {
        if (!s.status) return false;
        
        const statusToCheck = s.status.toLowerCase().trim();
        return filters.status.some(st => statusToCheck === st.toLowerCase().trim());
      });
    }
    
    // Reset chart filter when applying manual filters
    setChartFilter({ type: null, value: null });
    
    // Update filtered shipments
    setFilteredShipments(results);
  };

  // Apply chart filter
  const applyChartFilter = () => {
    // Start with all shipments
    let results = [...allShipments];
    
    // Apply existing manual filters first
    if (filters.date_range[0] && filters.date_range[1]) {
      const startDate = new Date(filters.date_range[0]);
      const endDate = new Date(filters.date_range[1]);
      results = results.filter(s => {
        const dateToCompare = s.pickup_date_formatted || s.created_at_date;
        if (!dateToCompare) return false;
        return dateToCompare >= startDate && dateToCompare <= endDate;
      });
    }
    
    if (filters.delivery_date_range[0] && filters.delivery_date_range[1]) {
      const startDate = new Date(filters.delivery_date_range[0]);
      const endDate = new Date(filters.delivery_date_range[1]);
      results = results.filter(s => {
        const dateToCompare = s.delivery_date;
        if (!dateToCompare) return false;
        return dateToCompare >= startDate && dateToCompare <= endDate;
      });
    }
    
    // Filter by cliente_id (permite filtrado por ID o por nombre)
    if (filters.cliente_id) {
      results = results.filter(s => {
        // Intentar coincidencia por ID
        if (s.cliente_id === parseInt(filters.cliente_id)) return true;
        
        // O por nombre del cliente si coincide con algún cliente en filterOptions
        const selectedClient = filterOptions.clients.find(c => c.id === parseInt(filters.cliente_id));
        if (selectedClient && s.client_name) {
          const clientNameLower = s.client_name.toLowerCase();
          const selectedClientNameLower = selectedClient.name.toLowerCase();
          if (clientNameLower.includes(selectedClientNameLower)) {
            return true;
          }
        }
        
        return false;
      });
    }
    
    // Filter by carrier_id (usando el campo Lt o carrier_name)
    if (filters.carrier_id && filters.carrier_id.length > 0) {
      results = results.filter(s => {
        if (!s.Lt && !s.carrier_name) return false;
        
        const carrierToCheck = (s.Lt || s.carrier_name).toLowerCase().trim();
        return filters.carrier_id.some(id => carrierToCheck === id.toLowerCase().trim());
      });
    }
    
    // Filter by vendor
    if (filters.vendor && filters.vendor.length > 0) {
      results = results.filter(s => {
        if (!s.vendor) return false;
        
        const vendorToCheck = s.vendor.toLowerCase().trim();
        return filters.vendor.some(v => vendorToCheck === v.toLowerCase().trim());
      });
    }
    
    // Filter by status (case insensitive)
    if (filters.status && filters.status.length > 0) {
      results = results.filter(s => {
        if (!s.status) return false;
        
        const statusToCheck = s.status.toLowerCase().trim();
        return filters.status.some(st => statusToCheck === st.toLowerCase().trim());
      });
    }
    
    // Luego aplicar el filtro del gráfico
    if (chartFilter.type === 'photo') {
      if (chartFilter.value === 'loaded') {
        results = results.filter(s => s.has_photo_evidence);
      } else {
        results = results.filter(s => !s.has_photo_evidence);
      }
    } else if (chartFilter.type === 'pdf') {
      if (chartFilter.value === 'loaded') {
        results = results.filter(s => s.has_pdf_evidence);
      } else {
        results = results.filter(s => !s.has_pdf_evidence);
      }
    }
    
    // Actualizar los embarques filtrados
    setFilteredShipments(results);
  };

  // Handle filter form submission
  const handleFilterSubmit = (e) => {
    e.preventDefault();
    setChartFilter({ type: null, value: null }); // Reset chart filter
    applyFilters();
    setShowFilters(false);
  };

  // Reset filters
  const handleResetFilters = () => {
    setFilters({
      date_range: [null, null],
      delivery_date_range: [null, null],
      cliente_id: '',
      carrier_id: [],
      status: [],
      vendor: []
    });
    
    // Reset chart filter
    setChartFilter({ type: null, value: null });
    
    // Reset filtered shipments to all shipments
    setFilteredShipments(allShipments);
    setShowFilters(false);
  };
  
  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: Array.isArray(prev[name]) ? [...prev[name], value] : value
    }));
  };

  // Toggle dropdown open/close state
  const toggleDropdown = (dropdown) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [dropdown]: !prev[dropdown]
    }));
  };

  // Handle multiple selection filters (checkbox style)
  const handleMultiSelectChange = (name, value) => {
    console.log(`DEBUG - handleMultiSelectChange - nombre: ${name}, valor: ${value}`);
    console.log(`DEBUG - Valores actuales en ${name}:`, filters[name]);
    
    setFilters(prev => {
      // Si el valor ya está seleccionado, lo eliminamos
      if (prev[name].includes(value)) {
        console.log(`DEBUG - Eliminando ${value} de ${name}`);
        return {
          ...prev,
          [name]: prev[name].filter(item => item !== value)
        };
      } 
      // Si no está seleccionado, lo agregamos
      else {
        console.log(`DEBUG - Agregando ${value} a ${name}`);
        return {
          ...prev,
          [name]: [...prev[name], value]
        };
      }
    });
  };

  // Handle "Select All" or "Deselect All" for a filter
  const handleSelectAllChange = (name, values) => {
    console.log(`DEBUG - handleSelectAllChange - nombre: ${name}`);
    console.log(`DEBUG - Valores a seleccionar todos:`, values);
    console.log(`DEBUG - Valores actuales:`, filters[name]);
    
    setFilters(prev => {
      // Si ya están todos seleccionados, deseleccionar todos
      if (prev[name].length === values.length) {
        console.log(`DEBUG - Deseleccionando todos de ${name}`);
        return {
          ...prev,
          [name]: []
        };
      } 
      // Si no están todos seleccionados, seleccionar todos
      else {
        console.log(`DEBUG - Seleccionando todos para ${name}:`, values);
        return {
          ...prev,
          [name]: values
        };
      }
    });
  };
  // Handle chart click
  const handleChartClick = (chart, type) => (event, elements) => {
    if (elements.length === 0) return;
    
    const index = elements[0].index;
    const value = index === 0 ? 'loaded' : 'not_loaded';
    
    // Desactivar filtro si se hace clic en la misma sección
    if (chartFilter.type === type && chartFilter.value === value) {
      setChartFilter({ type: null, value: null });
      setFilteredShipments(allShipments);
    } else {
      setChartFilter({ type, value });
      // applyChartFilter will be called via useEffect
    }
  };

  // Export table data to Excel
  const handleExportExcel = () => {
    // Función para formatear la fecha en formato dd/MM/yyyy HH:mm
    const formatExcelDate = (dateStr) => {
      if (!dateStr) return 'N/A';
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-MX', { 
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };
    
    // Función para procesar las fechas de entrega (pueden ser múltiples)
    const processDeliveryDates = (shipment) => {
      if (Object.keys(shipment.delivery_dates_by_stop).length === 0) {
        return 'N/A';
      } else if (Object.keys(shipment.delivery_dates_by_stop).length === 1) {
        return shipment.delivery_date_display;
      } else {
        // Si hay múltiples paradas, concatenarlas
        return Object.entries(shipment.delivery_dates_by_stop)
          .sort(([stopA], [stopB]) => {
            const numA = stopA === 'unknown' ? 999 : parseInt(stopA);
            const numB = stopB === 'unknown' ? 999 : parseInt(stopB);
            return numA - numB;
          })
          .map(([stop, data]) => {
            return `${stop === 'unknown' ? 'Parada' : `Parada ${stop}`}: ${data.display}`;
          })
          .join(' | ');
      }
    };    // Export all filtered data
    const calculatePhotoDelay = (shipment) => {
      if (!shipment.metrics || !shipment.metrics.photo_operador || !shipment.metrics.order_Delivery) {
        return 'N/A';
      }
      
      const photoTime = new Date(shipment.metrics.photo_operador);
      const deliveryTime = new Date(shipment.metrics.order_Delivery);
      
      if (isNaN(photoTime) || isNaN(deliveryTime)) {
        return 'N/A';
      }
      
      const diffHours = Math.round((photoTime - deliveryTime) / (1000 * 60 * 60));
      return `${diffHours} hrs`;
    };
    
    const calculatePdfDelay = (shipment) => {
      if (!shipment.metrics || !shipment.metrics.order_Podtime || !shipment.metrics.order_Delivery) {
        return 'N/A';
      }
      
      const pdfTime = new Date(shipment.metrics.order_Podtime);
      const deliveryTime = new Date(shipment.metrics.order_Delivery);
      
      if (isNaN(pdfTime) || isNaN(deliveryTime)) {
        return 'N/A';
      }
      
      const diffDays = Math.round((pdfTime - deliveryTime) / (1000 * 60 * 60 * 24));
      return `${diffDays} días`;
    };
    
    const worksheet = XLSX.utils.json_to_sheet(filteredShipments.map(s => ({
      Referencia: s.reference,
      Truck: s.truck || 'N/A',
      'Cliente (Vendor)': s.vendor !== 'N/A' ? s.vendor : s.client_name,
      'Fecha de Carga': s.pickup_date ? formatExcelDate(s.pickup_date) : 'N/A',
      'Cita de Entrega': processDeliveryDates(s),
      Origen: s.origin,
      Destino: s.destination,
      Estado: s.status,
      Transportista: s.carrier_name || 'N/A',
      'Evidencia Fotográfica': s.has_photo_evidence ? 'Sí' : 'No',
      'Retraso Foto (HR)': calculatePhotoDelay(s),
      'Evidencia PDF': s.has_pdf_evidence ? 'Sí' : 'No',
      'Retraso PDF (Días)': calculatePdfDelay(s)
    })));
    
    // Ajustar el ancho de las columnas
    const wscols = [
      { wch: 15 },  // Referencia
      { wch: 10 },  // Truck
      { wch: 20 },  // Cliente (Vendor)
      { wch: 20 },  // Fecha de Carga
      { wch: 30 },  // Cita de Entrega
      { wch: 20 },  // Origen
      { wch: 20 },  // Destino
      { wch: 12 },  // Estado
      { wch: 20 },  // Transportista
      { wch: 10 },  // Evidencia Fotográfica
      { wch: 12 },  // Retraso Foto (HR)
      { wch: 10 },  // Evidencia PDF
      { wch: 15 }   // Retraso PDF (Días)
    ];
    worksheet['!cols'] = wscols;
    
    // Create workbook and append worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Embarques');
    
    // Generate Excel file
    const today = new Date().toISOString().split('T')[0];
    XLSX.writeFile(workbook, `Reporte_Evidencias_${today}.xlsx`);
  };

  // Chart data for photo evidence
  const photoChartData = {
    labels: ['Evidencia Fotográfica Cargada', 'Evidencia Fotográfica No Cargada'],
    datasets: [
      {
        data: [stats.photo_evidence.loaded, stats.photo_evidence.not_loaded],
        backgroundColor: ['#4CAF50', '#F44336'],
        hoverBackgroundColor: ['#43A047', '#E53935'],
        borderWidth: 1
      }
    ]
  };

  // Chart data for PDF evidence
  const pdfChartData = {
    labels: ['Evidencia PDF Cargada', 'Evidencia PDF No Cargada'],
    datasets: [
      {
        data: [stats.pdf_evidence.loaded, stats.pdf_evidence.not_loaded],
        backgroundColor: ['#2196F3', '#FF9800'],
        hoverBackgroundColor: ['#1E88E5', '#F57C00'],
        borderWidth: 1
      }
    ]
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 20,
          boxWidth: 12,
          font: {
            size: 10
          },
          generateLabels: (chart) => {
            const datasets = chart.data.datasets;
            return chart.data.labels.map((label, i) => {
              const dataset = datasets[0];
              const value = dataset.data[i];
              const total = dataset.data.reduce((acc, val) => acc + val, 0);
              const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
              
              let shortLabel = '';
              if (label.includes('Fotográfica')) {
                shortLabel = i === 0 ? 'Con foto' : 'Sin foto';
              } else {
                shortLabel = i === 0 ? 'Con PDF' : 'Sin PDF';
              }
              
              return {
                text: `${shortLabel} (${percentage}%)`,
                fillStyle: dataset.backgroundColor[i],
                hidden: isNaN(value) || value === 0,
                index: i
              };
            });
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.raw || 0;
            const total = context.dataset.data.reduce((acc, curr) => acc + curr, 0);
            const percentage = Math.round((value / total) * 100);
            
            // Usar la etiqueta completa en el tooltip para mayor claridad
            return `${label}: ${value} (${percentage}%)`;
          }
        }
      }
    },
    onClick: null, // Will be set for each chart individually
    layout: {
      padding: {
        bottom: 20,
        left: 5,
        right: 5
      }
    }
  };

  // Calculate filter active state
  const isFilterActive = Object.values(filters).some(val => val !== '') || chartFilter.type !== null;

  // Función para obtener el título del filtro actual de gráfico
  const getChartFilterTitle = () => {
    if (!chartFilter.type) return null;
    
    const type = chartFilter.type === 'photo' ? 'fotográfica' : 'PDF';
    const status = chartFilter.value === 'loaded' ? 'cargada' : 'no cargada';
    
    return `Evidencia ${type} ${status}`;
  };

  return (
    <div className="flex flex-col h-full space-y-4">
      {/* Header section */}
      <div className="flex items-center justify-between bg-white p-4 rounded-md shadow">
        <h1 className="text-2xl font-bold text-gray-800">Dashboard de Evidencias</h1>
        
        <div className="flex space-x-3">
          {/* Active chart filter indicator */}
          {chartFilter.type && (
            <div className="bg-blue-100 text-blue-800 py-2 px-4 rounded-md flex items-center">
              <span>Filtrando por: {getChartFilterTitle()}</span>
              <button 
                onClick={() => setChartFilter({ type: null, value: null })}
                className="ml-2 text-blue-700 hover:text-blue-900"
              >
                <XCircle size={16} />
              </button>
            </div>
          )}
          
          {/* Filter toggle button */}
          <button 
            onClick={() => setShowFilters(!showFilters)} 
            className={`flex items-center space-x-2 px-4 py-2 rounded-md ${
              isFilterActive && !chartFilter.type ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
            }`}
          >
            {isFilterActive && !chartFilter.type ? (
              <FilterX size={18} />
            ) : (
              <Filter size={18} />
            )}
            <span>Filtros</span>
            {isFilterActive && !chartFilter.type && (
              <div className="bg-white text-blue-600 rounded-full w-5 h-5 flex items-center justify-center text-xs">
                {Object.values(filters).filter(v => v !== '').length}
              </div>
            )}
          </button>
          
          {/* Export button */}
          <button 
            onClick={handleExportExcel}
            className="flex items-center space-x-2 px-4 py-2 rounded-md bg-green-600 text-white"
            disabled={loading || filteredShipments.length === 0}
          >
            <Download size={18} />
            <span>Exportar</span>
          </button>
        </div>
      </div>
      
      {/* Chart help tooltip */}
      {showChartHelp && (
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4 relative">
          <div className="flex">
            <div className="flex-shrink-0">
              <Info className="h-5 w-5 text-blue-500" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                ¡Puedes hacer clic en cualquier sección de las gráficas para filtrar los embarques correspondientes!
              </p>
            </div>
            <button 
              onClick={() => setShowChartHelp(false)}
              className="absolute top-2 right-2 text-blue-700"
            >
              <XCircle size={16} />
            </button>
          </div>
        </div>
      )}
      
      {/* Filters section */}
      {showFilters && (
        <div className="bg-white p-4 rounded-md shadow">
          <form ref={filterFormRef} onSubmit={handleFilterSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Date range - Fecha de carga */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de carga</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 z-10">
                    <Calendar size={16} className="text-gray-400" />
                  </span>
                  <DatePicker
                    selected={filters.date_range[0] ? new Date(filters.date_range[0]) : null}
                    onChange={(dates) => {
                      // Asegurarnos de que start y end son objetos Date válidos o null
                      const [start, end] = dates;
                      const startDate = start ? new Date(start) : null;
                      const endDate = end ? new Date(end) : null;
                      setFilters(prev => ({ 
                        ...prev, 
                        date_range: [startDate, endDate] 
                      }));
                    }}
                    startDate={filters.date_range[0] ? new Date(filters.date_range[0]) : null}
                    endDate={filters.date_range[1] ? new Date(filters.date_range[1]) : null}
                    selectsRange
                    placeholderText="Seleccionar rango de fechas"
                    className="w-full pl-10 rounded-md border border-gray-300 py-2 pr-3 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    dateFormat="dd/MM/yyyy"
                  />
                </div>
              </div>
              
              {/* Date range - Cita de entrega */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cita de entrega</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 z-10">
                    <Calendar size={16} className="text-gray-400" />
                  </span>
                  <DatePicker
                    selected={filters.delivery_date_range[0] ? new Date(filters.delivery_date_range[0]) : null}
                    onChange={(dates) => {
                      // Asegurarnos de que start y end son objetos Date válidos o null
                      const [start, end] = dates;
                      const startDate = start ? new Date(start) : null;
                      const endDate = end ? new Date(end) : null;
                      setFilters(prev => ({ 
                        ...prev, 
                        delivery_date_range: [startDate, endDate] 
                      }));
                    }}
                    startDate={filters.delivery_date_range[0] ? new Date(filters.delivery_date_range[0]) : null}
                    endDate={filters.delivery_date_range[1] ? new Date(filters.delivery_date_range[1]) : null}
                    selectsRange
                    placeholderText="Seleccionar rango de fechas"
                    className="w-full pl-10 rounded-md border border-gray-300 py-2 pr-3 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    dateFormat="dd/MM/yyyy"
                  />
                </div>
              </div>
            </div>
            
            {/* Multi-select Filters with dropdowns */}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Cliente (Vendor) dropdown multi-select */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Clientes
                  <span className="ml-1 text-xs text-gray-500">
                    ({filters.vendor.length} seleccionados)
                  </span>
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => toggleDropdown('vendors')}
                    className="w-full bg-white border border-gray-300 rounded-md py-2 pl-3 pr-10 text-left focus:outline-none focus:ring-1 focus:ring-blue-500 flex justify-between items-center"
                  >
                    <span className="block truncate">
                      {filters.vendor.length === 0 ? 'Seleccionar clientes' : 
                       filters.vendor.length === filterOptions.vendors.length ? 'Todos los clientes' :
                       `${filters.vendor.length} cliente(s) seleccionado(s)`}
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M10 3a.75.75 0 01.55.24l3.25 3.5a.75.75 0 11-1.1 1.02L10 4.852 7.3 7.76a.75.75 0 01-1.1-1.02l3.25-3.5A.75.75 0 0110 3z" clipRule="evenodd" />
                      </svg>
                    </span>
                  </button>
                  
                  {openDropdowns.vendors && (
                    <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none text-sm">
                      <div className="p-2 border-b border-gray-200 bg-gray-50 sticky top-0">
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={filterOptions.vendors.length > 0 && filters.vendor.length === filterOptions.vendors.length}
                            onChange={() => handleSelectAllChange('vendor', filterOptions.vendors.map(v => v.toLowerCase()))}
                            className="rounded text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm font-medium">
                            {filterOptions.vendors.length > 0 && filters.vendor.length === filterOptions.vendors.length 
                              ? "Deseleccionar todos" 
                              : "Seleccionar todos"}
                          </span>
                        </label>
                      </div>
                      <div className="py-1 px-2">
                        {filterOptions.vendors.length === 0 ? (
                          <p className="text-sm text-gray-500 italic p-2">No hay clientes disponibles</p>
                        ) : (
                          <div className="max-h-40 overflow-auto">
                            {filterOptions.vendors.map(vendor => (
                              <label key={vendor} className="flex items-center space-x-2 px-2 py-1 hover:bg-gray-100 w-full">
                                <input
                                  type="checkbox"
                                  checked={filters.vendor.includes(vendor.toLowerCase())}
                                  onChange={() => handleMultiSelectChange('vendor', vendor.toLowerCase())}
                                  className="rounded text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm truncate">{vendor}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Transportista dropdown multi-select */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Transportistas
                  <span className="ml-1 text-xs text-gray-500">
                    ({filters.carrier_id.length} seleccionados)
                  </span>
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => toggleDropdown('carriers')}
                    className="w-full bg-white border border-gray-300 rounded-md py-2 pl-3 pr-10 text-left focus:outline-none focus:ring-1 focus:ring-blue-500 flex justify-between items-center"
                  >
                    <span className="block truncate">
                      {filters.carrier_id.length === 0 ? 'Seleccionar transportistas' : 
                       filters.carrier_id.length === filterOptions.carriers.length ? 'Todos los transportistas' :
                       `${filters.carrier_id.length} transportista(s) seleccionado(s)`}
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M10 3a.75.75 0 01.55.24l3.25 3.5a.75.75 0 11-1.1 1.02L10 4.852 7.3 7.76a.75.75 0 01-1.1-1.02l3.25-3.5A.75.75 0 0110 3z" clipRule="evenodd" />
                      </svg>
                    </span>
                  </button>
                  
                  {openDropdowns.carriers && (
                    <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none text-sm">
                      <div className="p-2 border-b border-gray-200 bg-gray-50 sticky top-0">
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={filterOptions.carriers.length > 0 && filters.carrier_id.length === filterOptions.carriers.length}
                            onChange={() => handleSelectAllChange('carrier_id', filterOptions.carriers.map(c => c.id.toLowerCase()))}
                            className="rounded text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm font-medium">
                            {filterOptions.carriers.length > 0 && filters.carrier_id.length === filterOptions.carriers.length 
                              ? "Deseleccionar todos" 
                              : "Seleccionar todos"}
                          </span>
                        </label>
                      </div>
                      <div className="py-1 px-2">
                        {filterOptions.carriers.length === 0 ? (
                          <p className="text-sm text-gray-500 italic p-2">No hay transportistas disponibles</p>
                        ) : (
                          <div className="max-h-40 overflow-auto">
                            {filterOptions.carriers.map(carrier => (
                              <label key={carrier.id} className="flex items-center space-x-2 px-2 py-1 hover:bg-gray-100 w-full">
                                <input
                                  type="checkbox"
                                  checked={filters.carrier_id.includes(carrier.id.toLowerCase())}
                                  onChange={() => handleMultiSelectChange('carrier_id', carrier.id.toLowerCase())}
                                  className="rounded text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm truncate">{carrier.name}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Estado dropdown multi-select */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estados
                  <span className="ml-1 text-xs text-gray-500">
                    ({filters.status.length} seleccionados)
                  </span>
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => toggleDropdown('statuses')}
                    className="w-full bg-white border border-gray-300 rounded-md py-2 pl-3 pr-10 text-left focus:outline-none focus:ring-1 focus:ring-blue-500 flex justify-between items-center"
                  >
                    <span className="block truncate">
                      {filters.status.length === 0 ? 'Seleccionar estados' : 
                       filters.status.length === filterOptions.statuses.length ? 'Todos los estados' :
                       `${filters.status.length} estado(s) seleccionado(s)`}
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M10 3a.75.75 0 01.55.24l3.25 3.5a.75.75 0 11-1.1 1.02L10 4.852 7.3 7.76a.75.75 0 01-1.1-1.02l3.25-3.5A.75.75 0 0110 3z" clipRule="evenodd" />
                      </svg>
                    </span>
                  </button>
                  
                  {openDropdowns.statuses && (
                    <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none text-sm">
                      <div className="p-2 border-b border-gray-200 bg-gray-50 sticky top-0">
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={filterOptions.statuses.length > 0 && filters.status.length === filterOptions.statuses.length}
                            onChange={() => handleSelectAllChange('status', filterOptions.statuses.map(s => s.toLowerCase()))}
                            className="rounded text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm font-medium">
                            {filterOptions.statuses.length > 0 && filters.status.length === filterOptions.statuses.length 
                              ? "Deseleccionar todos" 
                              : "Seleccionar todos"}
                          </span>
                        </label>
                      </div>
                      <div className="py-1 px-2">
                        {filterOptions.statuses.length === 0 ? (
                          <p className="text-sm text-gray-500 italic p-2">No hay estados disponibles</p>
                        ) : (
                          <div className="max-h-40 overflow-auto">
                            {filterOptions.statuses.map(status => (
                              <label key={status} className="flex items-center space-x-2 px-2 py-1 hover:bg-gray-100 w-full">
                                <input
                                  type="checkbox"
                                  checked={filters.status.includes(status.toLowerCase())}
                                  onChange={() => handleMultiSelectChange('status', status.toLowerCase())}
                                  className="rounded text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm truncate">{status}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Filter action buttons */}
            <div className="mt-4 flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleResetFilters}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Limpiar filtros
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Aplicar filtros
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Statistics and charts section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Photo evidence chart */}
        <div className="bg-white p-4 rounded-md shadow-sm">
          <div className="flex items-center space-x-2 mb-2">
            <Camera size={18} className="text-gray-600" />
            <h2 className="text-base font-medium">Evidencia Fotográfica</h2>
          </div>
          <p className="text-xs text-gray-500 mb-2">Haz clic para filtrar</p>
          <div className={`h-56 cursor-pointer ${chartFilter.type === 'photo' ? 'ring-1 ring-blue-400 rounded-lg p-1' : ''}`}>
            <Pie 
              data={photoChartData} 
              options={{
                ...chartOptions,
                onClick: handleChartClick(chartOptions, 'photo')
              }} 
            />
          </div>
        </div>
        
        {/* Total shipments counter */}
        <div className="bg-white p-4 rounded-md shadow-sm flex flex-col justify-center items-center">
          <div className="text-gray-500 text-sm mb-1">Total de Embarques</div>
          <div className="flex items-center">
            <Truck size={20} className="mr-2 text-gray-600" />
            <span className="text-3xl font-bold text-gray-800">{stats.total_shipments}</span>
          </div>
          {chartFilter.type && (
            <div className="mt-2 text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">
              {getChartFilterTitle()}
            </div>
          )}
        </div>
        
        {/* PDF evidence chart */}
        <div className="bg-white p-4 rounded-md shadow-sm">
          <div className="flex items-center space-x-2 mb-2">
            <FileText size={18} className="text-gray-600" />
            <h2 className="text-base font-medium">Evidencia PDF</h2>
          </div>
          <p className="text-xs text-gray-500 mb-2">Haz clic para filtrar</p>
          <div className={`h-56 cursor-pointer ${chartFilter.type === 'pdf' ? 'ring-1 ring-blue-400 rounded-lg p-1' : ''}`}>
            <Pie 
              data={pdfChartData} 
              options={{
                ...chartOptions,
                onClick: handleChartClick(chartOptions, 'pdf')
              }}
            />
          </div>
        </div>
      </div>
      
      {/* Shipments table section */}
      <div className="flex-grow bg-white p-4 rounded-md shadow">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Truck size={20} className="text-gray-600" />
            <h2 className="text-lg font-semibold">Embarques</h2>
            <span className="ml-2 bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs">
              {filteredShipments.length} embarques
            </span>
          </div>
          
          {/* Mostrar filtro activo de gráfico si existe */}
          {chartFilter.type && (
            <div className="flex items-center text-sm text-blue-600">
              <span>Mostrando: {getChartFilterTitle()}</span>
              <button 
                onClick={() => setChartFilter({ type: null, value: null })}
                className="ml-2 text-blue-700 hover:text-blue-900"
              >
                <XCircle size={16} />
              </button>
            </div>
          )}
        </div>
        
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : filteredShipments.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <XCircle size={48} className="mb-2" />
            <p>No se encontraron embarques</p>
          </div>
        ) : (
          <div className="max-h-[600px] overflow-y-auto">
            {/* Vista de tabla para pantallas medianas y grandes */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full divide-y divide-gray-200 table-fixed min-w-[900px]">
                <thead className="bg-gray-50 sticky top-0 z-10">                  <tr>
                    <th scope="col" className="w-[10%] px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Referencia
                    </th>
                    <th scope="col" className="w-[10%] px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Truck
                    </th>
                    <th scope="col" className="w-[12%] px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cliente (Vendor)
                    </th>
                    <th scope="col" className="w-[12%] px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fecha de Carga
                    </th>
                    <th scope="col" className="w-[12%] px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cita de Entrega
                    </th>
                    <th scope="col" className="w-[12%] px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Origen
                    </th>
                    <th scope="col" className="w-[12%] px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Destino
                    </th>
                    <th scope="col" className="w-[10%] px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th scope="col" className="w-[15%] px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Transportista
                    </th>
                    <th scope="col" className="w-[4%] px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Foto
                    </th>
                    <th scope="col" className="w-[6%] px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Retraso (HR)
                    </th>
                    <th scope="col" className="w-[5%] px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      PDF
                    </th>
                    <th scope="col" className="w-[6%] px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Retraso (Días)
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredShipments.map(shipment => (                    <tr key={shipment.id} className="hover:bg-gray-50">
                      <td className="px-2 py-3">
                        <div className="text-sm font-medium text-gray-900 truncate">{shipment.reference}</div>
                      </td>
                      <td className="px-2 py-3 text-sm text-gray-500">
                        <div className="truncate">{shipment.truck}</div>
                      </td>
                      <td className="px-2 py-3 text-sm text-gray-500">
                        <div className="truncate" title={shipment.vendor !== 'N/A' ? shipment.vendor : shipment.client_name}>
                          {shipment.vendor !== 'N/A' ? shipment.vendor : shipment.client_name}
                        </div>
                      </td>
                      <td className="px-2 py-3 text-sm text-gray-500">
                        {shipment.pickup_date ? new Date(shipment.pickup_date).toLocaleDateString('es-MX', { 
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : 'N/A'}
                      </td>
                      <td className="px-2 py-3 text-sm text-gray-500">
                        {Object.keys(shipment.delivery_dates_by_stop).length === 0 ? (
                          'N/A'
                        ) : Object.keys(shipment.delivery_dates_by_stop).length === 1 ? (
                          // Si solo hay una parada, mostrar directamente la fecha
                          <div className="truncate">
                            {shipment.delivery_date_display}
                          </div>
                        ) : (
                          // Si hay múltiples paradas, mostrar detalle por parada
                          <div className="space-y-1">
                            {Object.entries(shipment.delivery_dates_by_stop)
                              .sort(([stopA], [stopB]) => {
                                // Ordenar por número de parada
                                const numA = stopA === 'unknown' ? 999 : parseInt(stopA);
                                const numB = stopB === 'unknown' ? 999 : parseInt(stopB);
                                return numA - numB;
                              })
                              .map(([stop, data]) => (
                                <div key={stop} className="text-xs text-gray-700">
                                  • {stop === 'unknown' ? 'Parada' : `Parada ${stop}`}: {data.display}
                                </div>
                              ))}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-3 text-sm text-gray-500">
                        <div className="truncate" title={shipment.origin}>{shipment.origin}</div>
                      </td>
                      <td className="px-2 py-3 text-sm text-gray-500">
                        <div className="truncate" title={shipment.destination}>{shipment.destination}</div>
                      </td>
                      <td className="px-2 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          shipment.status === 'Completed' || shipment.status === 'FINALIZADO' ? 'bg-green-100 text-green-800' :
                          shipment.status === 'In Progress' || shipment.status === 'TRANSITO' ? 'bg-blue-100 text-blue-800' : 
                          'bg-gray-100 text-gray-800'
                        }`}>
                          <div className="truncate max-w-[90px]">{shipment.status}</div>
                        </span>
                      </td>
                      <td className="px-2 py-3 text-sm text-gray-500">
                        <div className="truncate" title={shipment.carrier_name || 'N/A'}>{shipment.carrier_name || 'N/A'}</div>
                      </td>                      <td className="px-2 py-3 text-center">
                        {shipment.has_photo_evidence ? (
                          <CheckCircle size={18} className="text-green-500 mx-auto" />
                        ) : (
                          <XCircle size={18} className="text-red-500 mx-auto" />
                        )}
                      </td>
                      <td className="px-2 py-3 text-center text-xs">
                        {shipment.metrics && shipment.metrics.photo_operador && shipment.metrics.order_Delivery ? (
                          (() => {
                            const photoTime = new Date(shipment.metrics.photo_operador);
                            const deliveryTime = new Date(shipment.metrics.order_Delivery);
                            if (isNaN(photoTime) || isNaN(deliveryTime)) return 'N/A';
                              const diffHours = Math.round((photoTime - deliveryTime) / (1000 * 60 * 60));
                            // Mostrar en verde si el retraso es menor a 24 horas
                            const colorClass = diffHours < 24 ? 'text-green-500 font-medium' : 'text-red-500 font-medium';
                            
                            return <span className={colorClass}>{diffHours} hrs</span>;
                          })()
                        ) : (
                          'N/A'
                        )}
                      </td>
                      <td className="px-2 py-3 text-center">
                        {shipment.has_pdf_evidence ? (
                          <CheckCircle size={18} className="text-green-500 mx-auto" />
                        ) : (
                          <XCircle size={18} className="text-red-500 mx-auto" />
                        )}
                      </td>
                      <td className="px-2 py-3 text-center text-xs">
                        {shipment.metrics && shipment.metrics.order_Podtime && shipment.metrics.order_Delivery ? (
                          (() => {
                            const pdfTime = new Date(shipment.metrics.order_Podtime);
                            const deliveryTime = new Date(shipment.metrics.order_Delivery);
                            if (isNaN(pdfTime) || isNaN(deliveryTime)) return 'N/A';
                              const diffDays = Math.round((pdfTime - deliveryTime) / (1000 * 60 * 60 * 24));
                            // Mostrar en verde si el retraso es menor o igual a 7 días
                            const colorClass = diffDays <= 7 ? 'text-green-500 font-medium' : 'text-red-500 font-medium';
                            
                            return <span className={colorClass}>{diffDays} días</span>;
                          })()
                        ) : (
                          'N/A'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Vista de tarjetas para móviles */}
            <div className="md:hidden space-y-4">
              {filteredShipments.map(shipment => (
                <div key={shipment.id} className="bg-white border rounded-lg p-4 shadow-sm">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-medium text-gray-900">{shipment.reference}</h3>
                    </div>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      shipment.status === 'Completed' || shipment.status === 'FINALIZADO' ? 'bg-green-100 text-green-800' :
                      shipment.status === 'In Progress' || shipment.status === 'TRANSITO' ? 'bg-blue-100 text-blue-800' : 
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {shipment.status}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="col-span-2">
                      <span className="font-medium text-gray-500">Fecha de Carga:</span> {' '}
                      {shipment.pickup_date ? new Date(shipment.pickup_date).toLocaleDateString('es-MX', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      }) : 'N/A'}
                    </div>
                    <div className="col-span-2">
                      <span className="font-medium text-gray-500">Cita de Entrega:</span> {' '}
                      {shipment.delivery_date_display}
                    </div>
                    <div className="col-span-2">
                      <span className="font-medium text-gray-500">Cliente:</span> {' '}
                      {shipment.client_name || 'N/A'}
                    </div>
                    <div className="col-span-2">
                      <span className="font-medium text-gray-500">Transportista:</span> {' '}
                      {shipment.carrier_name || 'N/A'}
                    </div>
                    <div>
                      <span className="font-medium text-gray-500">Origen:</span> {' '}
                      {shipment.origin}
                    </div>
                    <div>
                      <span className="font-medium text-gray-500">Destino:</span> {' '}
                      {shipment.destination}
                    </div>
                  </div>
                    <div className="flex items-center justify-between border-t mt-3 pt-3">
                    <div className="flex flex-col">
                      <div className="flex items-center space-x-1">
                        <span className="text-xs font-medium text-gray-500">Foto:</span>
                        {shipment.has_photo_evidence ? (
                          <CheckCircle size={16} className="text-green-500" />
                        ) : (
                          <XCircle size={16} className="text-red-500" />
                        )}
                      </div>
                      {shipment.metrics && shipment.metrics.photo_operador && shipment.metrics.order_Delivery && (
                        <div className="mt-1">
                          <span className="text-xs font-medium text-gray-500">Retraso (HR):</span>{' '}
                          {(() => {
                            const photoTime = new Date(shipment.metrics.photo_operador);
                            const deliveryTime = new Date(shipment.metrics.order_Delivery);
                            if (isNaN(photoTime) || isNaN(deliveryTime)) return 'N/A';
                              const diffHours = Math.round((photoTime - deliveryTime) / (1000 * 60 * 60));
                            // Mostrar en verde si el retraso es menor a 24 horas
                            const colorClass = diffHours < 24 ? 'text-green-500 font-medium' : 'text-red-500 font-medium';
                            
                            return <span className={`text-xs ${colorClass}`}>{diffHours} hrs</span>;
                          })()}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col">
                      <div className="flex items-center space-x-1">
                        <span className="text-xs font-medium text-gray-500">PDF:</span>
                        {shipment.has_pdf_evidence ? (
                          <CheckCircle size={16} className="text-green-500" />
                        ) : (
                          <XCircle size={16} className="text-red-500" />
                        )}
                      </div>
                      {shipment.metrics && shipment.metrics.order_Podtime && shipment.metrics.order_Delivery && (
                        <div className="mt-1">
                          <span className="text-xs font-medium text-gray-500">Retraso (Días):</span>{' '}
                          {(() => {
                            const pdfTime = new Date(shipment.metrics.order_Podtime);
                            const deliveryTime = new Date(shipment.metrics.order_Delivery);
                            if (isNaN(pdfTime) || isNaN(deliveryTime)) return 'N/A';
                              const diffDays = Math.round((pdfTime - deliveryTime) / (1000 * 60 * 60 * 24));
                            // Mostrar en verde si el retraso es menor o igual a 7 días
                            const colorClass = diffDays <= 7 ? 'text-green-500 font-medium' : 'text-red-500 font-medium';
                            
                            return <span className={`text-xs ${colorClass}`}>{diffDays} días</span>;
                          })()}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EvidenceDashboard;
